import {BaseTCIApplicationExecHistoryProvider} from "../tci-tenant-services";
import {
    BASE_CONSTANTS,
    BASE_URLS,
    ITCIApplicationExecHistoryProvider,
    ITCIExecHistoryDetailResponse,
    ITCIExecHistoryProcessListResponse,
    ITCIExecHistoryProcessResponse,
    ITCIExecHistoryResponse,
    TCIExecHistorySortableColumn,
    TCIExecHistoryStatus,
    TCITenantAppType
} from "@tci/common";
import {Injector} from "@angular/core";
import {Http} from "@angular/http";
import {Observable} from "rxjs";
import * as lodash from "lodash";

export class TciTenantBaseExecHistorySharedService extends BaseTCIApplicationExecHistoryProvider
    implements ITCIApplicationExecHistoryProvider {
    constructor(injector: Injector, http: Http) {
        super(injector, http);
    }

    /**
     * Get execution history list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: start time
     * @param processEndTime: end time
     * @param page: page number
     * @param limit: limit per page
     * @param processName: execution history item process name
     * @param status: execution history item status
     * @param searchText: search text
     * @param sortColumn: sortable column, default is startTime
     * @param sortDesc: sort order, default is true, descending
     */
    getExecHistory(appType: TCITenantAppType, appId: string,
                   processStartTime: number, processEndTime: number,
                   page?: number, limit?: number,
                   processName?: string, status?: TCIExecHistoryStatus, searchText?: string,
                   sortColumn?: TCIExecHistorySortableColumn, sortDesc?: boolean): Observable<ITCIExecHistoryResponse> {
        let url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.EXEC_HISTORY}/${appId}?`;
        url += `appType=${appType.toString()}`;
        url += `&processStartTime=${processStartTime}&processEndTime=${processEndTime}`;
        url += processName ? `&processName=${encodeURIComponent(processName)}` : "";
        url += status ? `&status=${status}` : "";
        url += searchText ? `&searchText=${encodeURIComponent(searchText)}` : "";
        url += sortColumn ? `&sortColumn=${sortColumn}` : "";
        if (sortColumn && lodash.isBoolean(sortDesc)) {
            url += "&sortDesc=" + sortDesc.toString();
        }
        url += `&page=${page}&limit=${limit}`;
        // default data for error response
        const response: ITCIExecHistoryResponse = {
            success: true,
            msg: "",
            result: [],
            total: 0,
            page: 1,
            limit: 0,
        };

        return this.getHttp().get(url)
            .map(res => res.json())
            .catch(err => {
                let msg = "";
                try {
                    msg = err.json().errorMsg;
                } catch (e) {
                    msg = "Error fetching execution history list.";
                }
                response.success = false;
                response.msg = msg;
                return Observable.throw(response);
            });
    }

    /**
     * Get execution history list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: start time
     * @param processEndTime: end time
     * @param processName: execution history item process name
     * @param appNodeName: app instance id
     * @param processInstanceJobId: process instance job id
     * @param sortColumn: sortable column, default is startTime
     * @param sortDesc: sort order, default is true, descending
     */
    getExecHistoryChildListByJobId(
        appType: TCITenantAppType, appId: string,
        processStartTime: number, processEndTime: number,
        processName: string, appNodeName: string, processInstanceJobId: string,
        sortColumn?: TCIExecHistorySortableColumn, sortDesc?: boolean
    ): Observable<ITCIExecHistoryProcessResponse> {
        let url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.EXEC_HISTORY}/${appId}/sub_process?`;
        url += `appType=${TCITenantAppType.fromJSON(appType).toString()}`;
        url += `&processStartTime=${processStartTime}&processEndTime=${processEndTime}`;
        url += processName ? `&processName=${encodeURIComponent(processName)}` : "";
        url += appNodeName ? `&appNodeName=${appNodeName}` : "";
        url += processInstanceJobId ? `&processInstanceJobId=${processInstanceJobId}` : "";
        url += sortColumn ? `&sortColumn=${sortColumn}` : "";
        if (sortColumn && lodash.isBoolean(sortDesc)) {
            url += "&sortDesc=" + sortDesc.toString();
        }
        // default data for error response
        const response: ITCIExecHistoryProcessResponse = {
            success: true,
            msg: "",
            result: []
        };

        return this.getHttp().get(url)
            .map(res => res.json())
            .catch(err => {
                let msg = "";
                try {
                    msg = err.json().errorMsg;
                } catch (e) {
                    msg = "Error fetching execution history child list.";
                }
                response.success = false;
                response.msg = msg;
                return Observable.throw(response);
            });
    }

    /**
     * Get execution history process list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: process start time
     * @param processEndTime: process end time
     */
    getExecHistoryProcessList(
        appType: TCITenantAppType, appId: string,
        processStartTime: number, processEndTime: number): Observable<ITCIExecHistoryProcessListResponse> {
        let url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.EXEC_HISTORY}/${appId}/process_list?`;
        url += `appType=${TCITenantAppType.fromJSON(appType).toString()}`;
        url += `&processStartTime=${processStartTime}`;
        url += `&processEndTime=${processEndTime}`;

        return this.getHttp().get(url)
            .map(res => res.json())
            .catch(err => {
                return Observable.throw([]);
            });
    }

    /**
     * Get execution history detail
     * @param appType: application type
     * @param appId: application Id
     * @param processName: execution history item process name
     * @param appNodeName: app instance id
     * @param processInstanceJobId: process instance job id
     * @param processStartTime: process start time
     * @param processEndTime: process end time
     */
    getExecHistoryDetail(appType: TCITenantAppType, appId: string,
                         processName: string, appNodeName: string, processInstanceJobId: string,
                         processStartTime: number, processEndTime: number): Observable<ITCIExecHistoryDetailResponse> {
        let url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.EXEC_HISTORY}/${appId}/process_detail?`;
        url += `appType=${TCITenantAppType.fromJSON(appType).toString()}`;
        url += processName ? `&processName=${encodeURIComponent(processName)}` : "";
        url += appNodeName ? `&appNodeName=${appNodeName}` : "";
        url += processInstanceJobId ? `&processInstanceJobId=${processInstanceJobId}` : "";
        url += `&processStartTime=${processStartTime}`;
        url += `&processEndTime=${processEndTime}`;
        // default data for error response
        const response: ITCIExecHistoryDetailResponse = {
            success: true,
            msg: "",
            result: {
                activity: [],
                transition: [],
            }
        };

        return this.getHttp().get(url)
            .map(res => res.json())
            .catch(err => {
                let msg = "";
                try {
                    msg = err.json().errorMsg;
                } catch (e) {
                    msg = "Error fetching execution history detail.";
                }
                response.success = false;
                response.msg = msg;
                return Observable.throw(response);
            });
    }
}
